export interface SkillPlugin {
  name: string;
  displayName: string;
  description: string | null;
  version: string | null;
  author: string | null;
  keywords: string[];
  readme: string | null;
  readmeUrl: string;
}

export interface Skill {
  plugin: SkillPlugin;
  name: string;
  description: string;
  department: string;
  category: string | null;
  path: string;
  htmlUrl: string;
  instructions: string;
}

import type { TagVariant } from './ds';
import { clearCatalogFilters } from './catalogFilters';

/* ---------------------------------------------------------------- *
 * Wire types — mirrored in server/src/types.ts
 * ---------------------------------------------------------------- */

export interface User {
  githubId: string;
  login: string;
  name: string | null;
  avatarUrl: string | null;
  initials: string;
  isCdpAdmin: boolean;
  /** Refreshed GitHub membership in the skills-pilot team; absent means no access. */
  isSkillsPilot?: boolean;
}

export type OnboardingStatus =
  | 'repo-created'
  | 'pending-platform-review'
  | 'infra-pr-merged'
  | 'infra-pr-closed'
  | 'unknown';

export interface InfraPullRequest {
  number: number;
  title: string;
  url: string;
  state: 'open' | 'closed';
  merged: boolean;
}

export interface AppReadiness {
  state: 'ready' | 'not-ready' | 'unavailable';
  devUrl: string | null;
  prodUrl: string | null;
  customProdUrl: string | null;
}

export interface AppSummary {
  /** GitHub's immutable repository ID. */
  repositoryId: string;
  /** Repo name, e.g. cdp-visit-summary-exporter */
  repo: string;
  /** Human title from the Firestore catalog. */
  title: string;
  description: string;
  appType?: string;
  owner: string;
  primaryOwner: string | null;
  creator: string | null;
  ownerUsers: string[];
  ownerGroups: string[];
  htmlUrl: string;
  visibility: 'public' | 'private';
  isOwner: boolean;
  canManage: boolean;
  pushedAt: string | null;
  status: OnboardingStatus;
  infraPullRequest: InfraPullRequest | null;
  archived: boolean;
  decommissionPullRequest: InfraPullRequest | null;
  readiness: AppReadiness;
}

export interface AppReadme {
  content: string;
  path: string;
  htmlUrl: string;
}

export type DeploymentEnvironment = 'dev' | 'prod';
export type DeploymentRunStatus = 'queued' | 'in_progress' | 'completed' | 'unknown';
export type DeploymentRunConclusion =
  | 'success'
  | 'failure'
  | 'cancelled'
  | 'timed_out'
  | 'skipped'
  | 'action_required'
  | 'neutral'
  | 'stale'
  | 'startup_failure'
  | null;

export interface DeploymentRun {
  id: number;
  runNumber: number;
  attempt: number;
  workflowName: string;
  environment: DeploymentEnvironment;
  status: DeploymentRunStatus;
  conclusion: DeploymentRunConclusion;
  commitSha: string;
  commitMessage: string;
  branchOrTag: string;
  actor: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  url: string;
}

export interface DeploymentHistory {
  cache?: { checkedAt: string; stale: boolean };
  source: 'github-actions';
  generatedAt: string;
  runs: DeploymentRun[];
}

/** Minimal catalog payload; full history remains behind the authorized single-app endpoint. */
export interface DeploymentStatusSnapshot {
  generatedAt: string;
  cache?: { checkedAt: string; stale: boolean };
  runs: Pick<DeploymentRun, 'environment' | 'status'>[];
}

export interface FailedDeploymentStep {
  jobName: string;
  stepName: string;
  output: string;
  truncated: boolean;
}

export interface DeploymentFailureDetails {
  runId: number;
  steps: FailedDeploymentStep[];
}

export interface ProductionPromotionRequest {
  tag: string;
}

export interface ProductionPromotion {
  tag: string;
  commitSha: string;
  commitMessage: string;
  devRunId: number;
}

export type SecretEnvironment = 'dev' | 'prod';

/** The optional GCP services an app can request at scaffold time. */
export type AppServiceKey =
  | 'cloudRunJobs'
  | 'cloudScheduler'
  | 'cloudStorage'
  | 'bigquery'
  | 'cloudSql';

/**
 * Every key is always present; absent means false, never undefined. The server
 * normalizes whatever the wizard sends into this shape regardless, so a partial
 * object here is a UI bug rather than a rendering hazard.
 */
export type AppServiceSelection = Record<AppServiceKey, boolean>;

export type AppRuntime = 'typescript' | 'python';

export type AppCapabilitySelection = AppServiceSelection & {
  secretsManagement: boolean;
};

export interface AppCapabilitySettings {
  enabled: AppCapabilitySelection;
  pending: AppCapabilitySelection;
  devOnly: boolean;
  proposedDevOnly: boolean;
  environmentChangePending: boolean;
  iapEnabled: boolean;
  proposedIapEnabled: boolean;
  iapChangePending: boolean;
  iapExceptionJustification: string | null;
  schedulerCron: string;
  schedulerTimeZone: string;
  schedulerChangePending: boolean;
  pullRequestUrl: string | null;
}

export type CloudSqlReadinessState = 'provisioning' | 'ready' | 'error';

export interface CloudSqlEnvironmentReadiness {
  environment: SecretEnvironment;
  projectId: string;
  instance: string;
  instanceConnectionName: string;
  database: string;
  runtimeUser: string;
  runtimeEnvironment: {
    CDP_CLOUD_SQL_INSTANCE: string;
    CDP_CLOUD_SQL_DATABASE: string;
    CDP_CLOUD_SQL_IAM_USER: string;
  };
  runtimeConfiguration: {
    service: boolean;
    job: boolean | null;
    ready: boolean;
  };
  state: CloudSqlReadinessState;
  message: string;
}

export interface AppCloudSqlReadiness {
  state: CloudSqlReadinessState;
  environments: CloudSqlEnvironmentReadiness[];
}

export interface CloudSqlRuntimeReconciliation {
  environment: SecretEnvironment;
  runtimeEnvironment: CloudSqlEnvironmentReadiness['runtimeEnvironment'];
  service: { name: string; status: 'reconciled' };
  job: { name: string; status: 'reconciled' } | null;
}

export interface AppOperatorTokenRequest {
  environment: 'dev' | 'prod';
  lifetimeSeconds?: number;
}

/**
 * Response of `POST /api/apps/:repo/operator-token`. Mirrored for completeness —
 * the browser has no use for `accessToken` and must not store or display it; the
 * MCP is the intended caller.
 */
export interface AppOperatorToken {
  serviceAccount: string;
  projectId: string;
  region: string;
  accessToken: string;
  expiresAt: string;
  scopes: string[];
  enabledServices: AppServiceKey[];
  resources: {
    cloudRunJob: string | null;
    cloudSchedulerJob: string | null;
    cloudStorageBucket: string | null;
    bigqueryDataset: string | null;
    cloudSqlInstance: string | null;
    cloudSqlDatabase: string | null;
    cloudSqlRuntimeUser: string | null;
  };
  /** @deprecated Retained during MCP rollout; callers should use resources. */
  resourcePrefix: string;
  /** @deprecated Retained during MCP rollout; callers should use resources. */
  datasetPrefix: string;
}

export type SecretsStatus = 'pending_infra' | 'ready' | 'error';

export interface SecretEnvironmentStatus {
  environment: SecretEnvironment;
  projectId: string;
  region: string;
  serviceName: string;
  secretWriterServiceAccount: string;
  ready: boolean;
  message?: string;
}

export interface AppSecretsStatus {
  status: SecretsStatus;
  environments: SecretEnvironmentStatus[];
}

export interface AppSecretSummary {
  environment: SecretEnvironment;
  envKey: string;
  secretId: string;
  /** Latest version created by the portal. */
  version: string;
  boundToCloudRun: boolean;
  boundVersion?: string;
  bindingState?: 'bound' | 'partial' | 'unbound';
}

export interface AppSecretChanges {
  environment: SecretEnvironment;
  updates: { envKey: string; value: string }[];
  removals: string[];
}

export interface TemplateParameterOption {
  name: string;
  title: string;
  description: string;
  required: boolean;
  type: 'string' | 'boolean';
  default?: string | boolean;
  pattern?: string;
}

export interface TemplateSummary {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  tags: string[];
  owner: string;
  /** Template steps, surfaced as the "what happens after you submit" list. */
  pipeline: { title: string; detail: string }[];
  available: boolean;
  unavailableReason?: string;
}

export interface ScaffoldContext {
  contractVersion: 2;
  isCdpAdmin: boolean;
  org: string;
  defaultTemplateId: string;
  repoNamePrefix: string;
  ownerTeams: string[];
  runtimes: {
    id: AppRuntime;
    label: string;
    jobEntrypoint: 'jobs/main.ts' | 'jobs/main.py';
  }[];
  scheduler: {
    defaultCron: string;
    timeZone: string;
  };
  layout: {
    appSourceDirectory: 'app';
    jobsDirectory: 'jobs';
    jobEntrypoint: 'jobs/main.ts';
  };
  defaults: {
    owner: string;
    runtime: 'typescript';
    visibility: 'public';
    enableSecretsManagement: false;
    services: AppServiceSelection;
  };
}

export interface ScaffoldStep {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'ok' | 'error' | 'skipped';
  detail?: string;
  url?: string;
}

export interface ScaffoldResult {
  steps: ScaffoldStep[];
  repoUrl?: string;
  infraPullRequestUrl?: string;
  ok: boolean;
}

/** Mirrors server/src/types.ts ScaffoldMessage. */
export type ScaffoldMessage =
  | { type: 'progress'; steps: ScaffoldStep[] }
  | { type: 'done'; result: ScaffoldResult }
  | { type: 'error'; message: string };

export interface ScaffoldRequest {
  templateId: string;
  appName: string;
  description: string;
  appType?: string;
  owner?: string;
  repoName: string;
  runtime: AppRuntime;
  enableSecretsManagement: boolean;
  devOnly?: boolean;
  environmentModeReason?: string;
  environmentModeConfirmation?: string;
  services: AppServiceSelection;
  schedulerCron: string;
  isPublic: boolean;
}

export interface CatalogAccessUpdate {
  visibility: 'public' | 'private';
  owner: string;
  ownerUsers: string[];
  ownerGroups: string[];
}

export interface OwnershipTransferRequest {
  newOwner: string;
  keepCurrentOwner: boolean;
}

export interface AppDetailsUpdate {
  title: string;
  description: string;
  appType?: string | null;
}

export interface ApplicationAccessUpdate {
  environment: 'dev' | 'prod' | 'both';
  members: string[];
  allAbridgeUsers: boolean;
}

export interface ApplicationAccess {
  both: string[];
  dev: string[];
  prod: string[];
  allAbridgeUsers: { dev: boolean; prod: boolean };
}

export interface ApplicationAccessReadiness {
  dev: { ready: boolean; iapEnabled: boolean | null; message?: string };
  prod: { ready: boolean; iapEnabled: boolean | null; message?: string };
}

export interface LiveSessionStart {
  token: string;
  expiresAt: string;
  preflightUrl: string;
  workspacePath: string;
  mcpConfigPath: string;
}

/* ---------------------------------------------------------------- *
 * Client
 * ---------------------------------------------------------------- */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const REAUTH_ATTEMPT_KEY = 'cdp_reauthentication_attempted';
const REAUTH_RETURN_HASH_KEY = 'cdp_reauthentication_return_hash';

interface ApiErrorBody {
  message?: string;
  error?: string;
}

function handleAuthenticationFailure(errorCode: string | undefined): void {
  if (errorCode === 'organization_membership_required' || errorCode === 'organization_invitation_pending') {
    clearCatalogFilters();
    window.location.assign(`/?auth_error=${errorCode}`);
    return;
  }
  if (errorCode !== 'reauthentication_required') return;
  clearCatalogFilters();
  if (sessionStorage.getItem(REAUTH_ATTEMPT_KEY)) return;

  sessionStorage.setItem(REAUTH_ATTEMPT_KEY, 'true');
  if (window.location.hash.startsWith('#/')) {
    sessionStorage.setItem(REAUTH_RETURN_HASH_KEY, window.location.hash);
  }
  window.location.assign('/api/auth/login');
}

async function responseError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    /* non-JSON error body */
  }
  handleAuthenticationFailure(body.error);
  return new ApiError(
    body.message ?? body.error ?? `Request failed (${response.status})`,
    response.status,
    body.error,
  );
}

export function completeAutomaticReauthentication(): void {
  sessionStorage.removeItem(REAUTH_ATTEMPT_KEY);
  const returnHash = sessionStorage.getItem(REAUTH_RETURN_HASH_KEY);
  sessionStorage.removeItem(REAUTH_RETURN_HASH_KEY);
  if (returnHash?.startsWith('#/') && window.location.hash !== returnHash) {
    window.location.hash = returnHash;
  }
}

export function beginManualSignIn(): void {
  sessionStorage.removeItem(REAUTH_ATTEMPT_KEY);
  window.location.assign('/api/auth/login');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw await responseError(response);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  validateSkillPlugin: (file: File, department: string) => request<{ name: string; department: string; skills: string[]; fileCount: number; normalizations?: string[] }>(`/skills/validate?department=${encodeURIComponent(department)}`, { method: 'POST', headers: { 'content-type': 'application/zip' }, body: file }),
  submitSkillPlugin: (file: File, department: string) => request<{ url: string }>(`/skills/submit?department=${encodeURIComponent(department)}`, { method: 'POST', headers: { 'content-type': 'application/zip' }, body: file }),
  skills: () => request<{ skills: Skill[]; repositoryUrl: string }>('/skills'),
  vibeCheck: (messages: VibeCheckMessage[]) => request<VibeCheckResponse>('/vibe-check', {
    method: 'POST', body: JSON.stringify({ messages }),
  }),
  me: () => request<{ user: User | null }>('/me'),
  apps: () => request<{ apps: AppSummary[] }>('/apps'),
  app: (repo: string) => request<{ app: AppSummary }>(`/apps/${encodeURIComponent(repo)}`),
  readme: (repo: string) =>
    request<{ readme: AppReadme | null }>(`/apps/${encodeURIComponent(repo)}/readme`),
  deploymentStatuses: (repos: string[]) => request<{ histories: Record<string, DeploymentStatusSnapshot>; unavailable: string[] }>('/catalog/deployment-statuses', { method: 'POST', body: JSON.stringify({ repos }) }),
  deployments: (repo: string) =>
    request<DeploymentHistory>(`/apps/${encodeURIComponent(repo)}/deployments`),
  deploymentFailure: (repo: string, runId: number) =>
    request<DeploymentFailureDetails>(
      `/apps/${encodeURIComponent(repo)}/deployments/${encodeURIComponent(String(runId))}/failure`,
    ),
  promoteToProduction: (repo: string, body: ProductionPromotionRequest) =>
    request<{ promotion: ProductionPromotion }>(`/apps/${encodeURIComponent(repo)}/promotions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cloudRunUrl: (repo: string, environment: SecretEnvironment = 'dev') =>
    request<{ url: string | null }>(
      `/apps/${encodeURIComponent(repo)}/cloud-run-url?environment=${environment}`,
    ),
  secretsStatus: (repo: string) =>
    request<AppSecretsStatus>(`/apps/${encodeURIComponent(repo)}/secrets/status`),
  secretsOverview: (repo: string, environment: SecretEnvironment) =>
    request<{ status: AppSecretsStatus; secrets: AppSecretSummary[] }>(
      `/apps/${encodeURIComponent(repo)}/secrets/overview?environment=${environment}`,
    ),
  secrets: (repo: string, environment: SecretEnvironment) =>
    request<{ secrets: AppSecretSummary[] }>(
      `/apps/${encodeURIComponent(repo)}/secrets?environment=${environment}`,
    ),
  applySecretChanges: (repo: string, changes: AppSecretChanges) =>
    request<void>(`/apps/${encodeURIComponent(repo)}/secrets`, {
      method: 'PUT',
      body: JSON.stringify(changes),
    }),
  deleteSecret: (repo: string, environment: SecretEnvironment, envKey: string) =>
    request<void>(
      `/apps/${encodeURIComponent(repo)}/secrets/${environment}/${encodeURIComponent(envKey)}`,
      { method: 'DELETE' },
    ),
  retrySecretBinding: (repo: string, environment: SecretEnvironment, envKey: string) =>
    request<void>(
      `/apps/${encodeURIComponent(repo)}/secrets/${environment}/${encodeURIComponent(envKey)}/binding`,
      { method: 'POST' },
    ),
  capabilitySettings: (repo: string) =>
    request<AppCapabilitySettings>(`/apps/${encodeURIComponent(repo)}/capabilities`),
  requestEnvironmentMode: (repo: string, devOnly: boolean, confirmation: string, reason: string) =>
    request<AppCapabilitySettings>(`/apps/${encodeURIComponent(repo)}/environment-mode`, {
      method: 'PUT',
      body: JSON.stringify({ devOnly, confirmation, reason }),
    }),
  cloudSqlReadiness: (repo: string) =>
    request<AppCloudSqlReadiness>(`/apps/${encodeURIComponent(repo)}/cloud-sql/readiness`),
  reconcileCloudSqlRuntime: (repo: string, environment: SecretEnvironment) =>
    request<CloudSqlRuntimeReconciliation>(
      `/apps/${encodeURIComponent(repo)}/cloud-sql/runtime/${environment}`,
      { method: 'POST' },
    ),
  requestCapabilityEnablement: (
    repo: string,
    capabilities: AppCapabilitySelection,
    schedulerCron: string,
  ) =>
    request<AppCapabilitySettings>(`/apps/${encodeURIComponent(repo)}/capabilities`, {
      method: 'PUT',
      body: JSON.stringify({ capabilities, schedulerCron }),
    }),
  requestIapChange: (repo: string, enabled: boolean, justification?: string) =>
    request<AppCapabilitySettings>(`/apps/${encodeURIComponent(repo)}/capabilities/iap`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, justification }),
    }),
  orgMembers: (query: string) =>
    request<{ users: { login: string }[] }>(`/org-members?q=${encodeURIComponent(query)}`),
  orgTeams: (query: string) =>
    request<{ teams: { slug: string; name: string }[] }>(`/org-teams?q=${encodeURIComponent(query)}`),
  updateCatalogAccess: (repo: string, body: CatalogAccessUpdate) =>
    request<{ app: AppSummary }>(`/apps/${encodeURIComponent(repo)}/catalog-access`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  updateAppDetails: (repo: string, body: AppDetailsUpdate) =>
    request<{ app: AppSummary }>(`/apps/${encodeURIComponent(repo)}/details`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  transferOwnership: (repo: string, body: OwnershipTransferRequest) =>
    request<{ app: AppSummary }>(`/apps/${encodeURIComponent(repo)}/ownership`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  applicationAccess: (repo: string) =>
    request<ApplicationAccess>(`/apps/${encodeURIComponent(repo)}/application-access`),
  applicationAccessReadiness: (repo: string) =>
    request<ApplicationAccessReadiness>(`/apps/${encodeURIComponent(repo)}/application-access/readiness`),
  updateApplicationAccess: (repo: string, body: ApplicationAccessUpdate) =>
    request<ApplicationAccess>(`/apps/${encodeURIComponent(repo)}/application-access`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  startLiveSession: (repo: string) =>
    request<{ liveSession: LiveSessionStart }>(`/apps/${encodeURIComponent(repo)}/live-session`, { method: 'POST' }),
  archiveApp: (repo: string) =>
    request<{ app: AppSummary }>(`/apps/${encodeURIComponent(repo)}/archive`, { method: 'POST' }),
  restoreApp: (repo: string) =>
    request<{ app: AppSummary }>(`/apps/${encodeURIComponent(repo)}/restore`, { method: 'POST' }),
  requestPermanentDeletion: (repo: string, confirmation: string) =>
    request<{ pullRequest: InfraPullRequest }>(`/apps/${encodeURIComponent(repo)}/permanent-deletion`, {
      method: 'POST',
      body: JSON.stringify({ confirmation }),
    }),
  templates: () => request<{ templates: TemplateSummary[] }>('/templates'),
  scaffoldContext: () => request<{ context: ScaffoldContext }>('/scaffold-context'),
  checkRepoName: (name: string) =>
    request<{ available: boolean; reason?: string }>(
      `/repo-name-available?name=${encodeURIComponent(name)}`,
    ),
  /**
   * Runs the scaffold and reports each step as it happens.
   *
   * The response is newline-delimited JSON read incrementally, so `onProgress`
   * fires while the work is still going. Chunk boundaries do not respect line
   * boundaries, hence the buffer.
   */
  scaffold: async (
    body: ScaffoldRequest,
    onProgress: (steps: ScaffoldStep[]) => void,
    signal?: AbortSignal,
  ): Promise<ScaffoldResult> => {
    const response = await fetch('/api/scaffold', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', accept: 'application/x-ndjson' },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });

    // Validation failures still answer with a normal JSON error before streaming.
    if (!response.ok) {
      throw await responseError(response);
    }
    if (!response.body) throw new ApiError('This browser cannot read a streamed response.', 500);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: ScaffoldResult | undefined;

    const handle = (line: string): void => {
      if (!line.trim()) return;
      const message = JSON.parse(line) as ScaffoldMessage;
      if (message.type === 'progress') onProgress(message.steps);
      else if (message.type === 'done') result = message.result;
      else throw new ApiError(message.message, 500);
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newline = buffer.indexOf('\n');
      while (newline !== -1) {
        handle(buffer.slice(0, newline));
        buffer = buffer.slice(newline + 1);
        newline = buffer.indexOf('\n');
      }
    }
    handle(buffer); // a final line without a trailing newline

    if (!result) {
      // The stream ended without a verdict: the connection dropped mid-run. The
      // server-side work may well have completed, so say so rather than implying
      // nothing happened.
      throw new ApiError(
        'The connection closed before setup finished. Check the app list — the repo may have been created.',
        500,
      );
    }
    return result;
  },
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
};

/** Human label + Tag variant for an onboarding status, matching the mock. */
export function statusPresentation(status: OnboardingStatus): {
  label: string;
  variant: TagVariant;
} {
  switch (status) {
    case 'infra-pr-merged':
      return { label: 'Infra request approved', variant: 'warning' };
    case 'pending-platform-review':
      return { label: 'Pending platform review', variant: 'warning' };
    case 'infra-pr-closed':
      return { label: 'Infra PR closed', variant: 'danger' };
    case 'repo-created':
      return { label: 'Repo created', variant: 'default' };
    default:
      return { label: 'Status unknown', variant: 'default' };
  }
}

/** "2 hours ago" / "6 days ago" / "—", as the mock's Last deploy column reads. */
export function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  for (const [unit, secondsPer] of units) {
    if (seconds >= secondsPer) {
      return formatter.format(-Math.floor(seconds / secondsPer), unit);
    }
  }
  return 'just now';
}

/* The repo-name derivation moved to components/RepoNameField.tsx, which owns the
   locked `cdp-` prefix and therefore the slug/suffix split. */

/** Inline conversation context only; never part of app provisioning. */
export interface VibeCheckAttachment {
  name: string;
  mediaType: 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif' | 'application/pdf' | 'text/plain' | 'text/markdown' | 'text/csv' | 'application/json' | 'text/html';
  data: string; // Base64 bytes held only in memory.
}
export interface VibeCheckMessage {
  role: 'user' | 'assistant';
  content: string;
  attachments?: VibeCheckAttachment[];
}
export interface VibeCheckProposal {
  appName: string;
  description: string;
  runtime: AppRuntime;
  enableSecretsManagement: boolean;
  services: AppServiceSelection;
  schedulerCron: string;
}
export type VibeCheckContext = Record<'purpose' | 'people' | 'information' | 'connections' | 'usage', boolean>;
export interface VibeCheckResponse {
  context: VibeCheckContext;
  message: string;
  assessment: 'exploring' | 'fits' | 'conditional' | 'outside-scope';
  conditions: string[];
  proposal: VibeCheckProposal | null;
  catalogVersion: string;
}
